

















import Vue from 'vue';
import BaseModel from '../../plugins/servicelayer/serviceTypes/baseModel';
import IValidationError from '../../plugins/validation/IValidationError';
import mixins from '../../mixins';
import IPFormElement from './IPFormElement';

export default mixins().extend({
  props: {
    tag: { type: [String, Object], default: () => 'div' },
    model: { type: Object, default: () => undefined },
    schema: { type: Object, default: () => undefined },
    noSummary: { type: Boolean, default: false },
    noScroll: { type: Boolean, default: false },
  },
  data: () => ({
    isMouseDown: false,
    inputElements: {} as Record<string, IPFormElement>,
    formElements: {} as Record<string, IPFormElement>,
    // TODO: Solve blur validation without using a "global" state
    isBlurValidation: false,
  }),
  watch: {
    model() {
      Object.keys(this.inputElements).forEach((propName) => {
        this.configureValidation(propName, this.inputElements[propName]);
      });
    },
    'model.errors': {
      handler() {
        const model = this.model as BaseModel<any>;
        this.$withoutWatchers(() => {
          // Sort by visual order
          model?.errors?.forEach((error) => {
            const element = this.getElementFromError(error);
            if (element) {
              error.sort = this.getElementY(element.$el);
            }
          });
          model.errors = Vue.$validation.sortErrors(model.errors);

          this.showErrorsOnElements();
        });
      },
    },
  },
  computed: {
    errorSummary() {
      const { errors } = (this.model as BaseModel<any>);
      if (errors) {
        return errors.map((error) => {
          const inputElement = this.getElementFromError(error) as IPFormElement;
          let formLabel;
          if (inputElement) {
            if (typeof inputElement.getLabel === 'function') {
              formLabel = inputElement.getLabel();
            } else {
              this.$log.warning('Unable to read label of component, it does not provide a getLabel method');
              this.$log.warning(inputElement);
            }
          }
          return { message: error.message, label: error.label ?? formLabel };
        });
      }
      return [];
    },
  },
  beforeMount() {
    document.addEventListener('mousedown', this.onGlobalMouseDown);
    document.addEventListener('mouseup', this.onGlobalMouseUp);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onGlobalMouseDown);
    document.removeEventListener('mouseup', this.onGlobalMouseUp);
  },
  methods: {
    onGlobalMouseDown() {
      this.isMouseDown = true;
    },
    onGlobalMouseUp() {
      this.isMouseDown = false;
    },
    getBestPathMatch(path: string, possiblePaths: string[]) {
      if (possiblePaths.includes(path)) {
        return path;
      }
      const bestGuesses = [] as string[];
      const pathSegments = path.split('.');
      possiblePaths.forEach((possiblePath) => {
        const possiblePathSegments = possiblePath.split('.');
        let matchedSegmentCount = 0;
        while (possiblePathSegments.length > 0) {
          const current = possiblePathSegments.pop();
          if (current) {
            const foundSegmentIndex = pathSegments.indexOf(current);
            if (foundSegmentIndex >= 0) {
              matchedSegmentCount += 1;
            }
          }
        }
        if (matchedSegmentCount > 1) {
          bestGuesses.push(possiblePath);
        }
      });
      bestGuesses.sort((a, b) => b?.split('.').length - a?.split('.').length);
      if (bestGuesses.length === 0) {
        const pathPropName = pathSegments[pathSegments.length - 1];
        if (possiblePaths.includes(pathPropName)) {
          bestGuesses.push(pathPropName);
        }
      }
      const propName = bestGuesses[bestGuesses.length - 1];
      return propName;
    },
    getElementFromError(error?: IValidationError) {
      // TODO: Make this more precise by finding
      // the exact property path that was bound
      // instead of using heuristics
      const errorPropName = error?.path?.join('.') || error?.propName;
      if (!errorPropName) {
        return undefined;
      }
      let propName;
      // Sort by length so it matches with the correct depth
      // To be more precise it would have to compare how many delimiters "." there are
      const keys = Object.keys(this.inputElements).sort((a, b) => a.length - b.length);
      keys.forEach((e) => {
        if (e.endsWith(errorPropName)) {
          propName = e;
        }
      });

      if (!propName) {
        // Time to guess which one it could be via comparing path segments
        // Chance of false positives is negligable
        propName = this.getBestPathMatch(errorPropName, keys);
      }
      if (!propName) {
        return undefined;
      }
      let element = this.inputElements[propName];
      if (!element) {
        Object.values(this.inputElements).forEach((e) => {
          const canGetLabel = typeof e?.getLabel === 'function';
          if (canGetLabel && e.getLabel() === error?.label) {
            element = e;
          }
        });
      }
      return element;
    },
    getElementY(el?: Element | null) {
      if (!el) return -1;
      const rect = el.getBoundingClientRect();
      return rect.y;
    },
    onSubmit(event: Event) {
      event.preventDefault();
      this.$emit('submit', event);
      return false;
    },
    showErrorsOnElement(element: IPFormElement, errors: string | string[]) {
      if (element && typeof element.setErrors === 'function') {
        element.setErrors(errors);
      }
    },
    showErrorsOnElements() {
      const model = this.model as BaseModel<any>;
      // Clear all
      Object.keys(this.inputElements).forEach((propName) => {
        this.showErrorsOnElement(this.inputElements[propName], []);
        this.showErrorsOnElement(this.formElements[propName], []);
      });

      // Set all errors
      model.errors.forEach((error) => {
        // TODO: Make this work with nested propNames
        const { propName } = error;
        const inputElement = this.getElementFromError(error);
        if (propName && inputElement) {
          this.showErrorsOnElement(inputElement, [error.message]);
          this.showErrorsOnElement(this.formElements[propName], [error.message]);
        }
      });

      // Scroll first element into view
      if (!this.isBlurValidation) {
        const [error] = model.errors;
        const inputElement = this.getElementFromError(error);
        if (inputElement && inputElement.$el && !this.noScroll) {
          if (typeof inputElement.$el.scrollIntoView === 'function') {
            inputElement.$el.scrollIntoView();
          }
        }
      }
    },
    configureValidation(propPath: string, element: IPFormElement) {
      const schema = this.schema ?? this.model?.schema;
      if (schema === undefined) {
        return;
      }
      let propName = propPath;
      let propObjSchema: typeof schema;
      const propPathSplit = propPath?.split('.') ?? [];
      if (propPathSplit.length > 1) {
        const propObjPathSplit = propPathSplit?.splice(0, propPathSplit.length - 1);
        propName = propPathSplit[propPathSplit.length - 1];
        propObjSchema = Vue.$validation.getPropertySchema(schema, propObjPathSplit);
      }
      if (!propObjSchema) {
        propObjSchema = schema;
      }
      if (Array.isArray(propObjSchema?.required)) {
        const requiredFields = propObjSchema.required as string[];
        const isRequired = !!requiredFields.find((name) => name === propName);
        if (isRequired === true && typeof element.setRequired === 'function') {
          element.setRequired(isRequired);
        }
      }
    },
    getVModelPropName(element: IPFormElement) {
      const model = (element as any)?.$vnode?.data?.model;
      const expression = model?.expression as string;
      let propName = '';
      if (!expression) {
        return undefined;
      }
      const expressionSplit = expression?.split('.');
      if (expressionSplit.length > 1) {
        propName += expressionSplit.splice(1, expressionSplit.length).join('.');
      } else {
        propName += expression;
      }
      return propName === '' ? undefined : propName;
    },
    getVModelPropNameRecursive(element: IPFormElement) {
      let current = element as Vue;
      const propNameParts = [];
      while (current) {
        const vmodelPropName = this.getVModelPropName(current as any);
        if (vmodelPropName) {
          propNameParts.unshift(vmodelPropName);
        }
        current = current.$parent;
      }
      const propName = propNameParts.join('.');
      return propName;
    },
    performBlurValidation(propName: string) {
      if (this.model) {
        this.$nextTick(() => {
          const model = this.model as BaseModel<any>;
          this.isBlurValidation = true;
          const schema = this.schema ?? model.schema;
          const allErrors = Vue.$validation.validate(schema, model);
          // Combine current errors in case of clearing remaining
          // errors that aren't any anymore
          const allErrorPaths = [
            ...allErrors.map((e) => e.path.join('.')),
            ...model.errors.map((e) => e.path.join('.')),
          ];
          const bestGuessPropName = this.getBestPathMatch(propName, allErrorPaths);
          if (propName) {
            this.showErrorsOnElement(this.inputElements[propName], []);
          }
          if (bestGuessPropName) {
            model.validateWithSchema(schema, bestGuessPropName);
          }
          this.$nextTick(() => {
            this.isBlurValidation = false;
          });
        });
      }
    },
    registerBlurValidation(element: IPFormElement, propName: string) {
      // TODO: Make validate-on-blur configurable via prop
      element.$on('blur', () => {
        const mouseUpListener = () => {
          document.removeEventListener('mouseup', mouseUpListener);
          this.performBlurValidation(propName);
        };
        if (this.isMouseDown) {
          // The error messages can cause buttons to move and then any click would miss the buttons
          // Since the blur event is fired directly after mousedown we need to wait for the mouseup to happen
          // This way it is clear that the click was done before the layout is changed by the validation
          document.addEventListener('mouseup', mouseUpListener);
        } else {
          this.performBlurValidation(propName);
        }
      });
    },
    onFormElementRegistered(elementChild: IPFormElement) {
      const inputElement = elementChild.$parent?.$parent as IPFormElement;
      const propName = this.getVModelPropNameRecursive(inputElement);
      // eslint-disable-next-line no-underscore-dangle
      const elementKey = propName ?? (inputElement as any)._uid.toString();
      try {
        const schema = this.schema ?? this.model?.schema;
        const schemaPropPath = [...(elementKey.split('.'))];
        let propSchema;
        while (!propSchema && schemaPropPath.length > 0) {
          propSchema = Vue.$validation.getPropertySchema(schema, schemaPropPath);
          schemaPropPath.shift();
        }
        const title = propSchema?.title;
        // const description = propSchema?.description;
        if (title) {
          inputElement.setLabel(this.$tAlt(title, title));
        }
      } catch {
        // Do nothing
      }

      const pFormElement = elementChild.$parent as IPFormElement;
      this.formElements[elementKey] = pFormElement;
      this.configureValidation(elementKey, pFormElement);

      // eslint-disable-next-line no-underscore-dangle
      this.inputElements[elementKey] = inputElement;
      this.configureValidation(elementKey, inputElement);
      this.registerBlurValidation(inputElement, elementKey);
    },
  },
});
